import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, useNavigate } from 'react-router';
import { Button, Checkbox, Form, Input, Popconfirm } from 'antd';

import { Container } from '@/ui';
import { useDocumentSection } from '@/features/constructorDocuments/hooks';
import { DocumentTypeList } from '@/features/constructorDocuments/components/DocumentTypeList';
import { DocumentType } from '@/features/constructorDocuments/components/DocumentType';
import { Box, NestedBox, ActionBox } from '@/features/constructorDocuments/styled';
import { areArraysEqual } from '@/utils/helpers';
import { ConstructorForm } from '@/components/admin/molecules';

export const DocumentSection = () => {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();

  const { data, isError, isLoading, handleCreate, handleUpdate, handleChangeRole, isUpdating } =
    useDocumentSection(id);
  const [documentSection, setDocumentSection] = useState({});
  const [showCreateNew, setShowCreateNew] = useState(false);
  const { handleDelete, isDeleting } = useDocumentSection(id);
  useEffect(() => {
    if (!data?.id) return;

    setDocumentSection(data);
  }, [data]);

  const onChange = (e) => {
    const { name, value } = e.target;
    setDocumentSection({ ...documentSection, [name]: value });
  };
  const onDelete = () => {
    handleDelete(documentSection);
    navigate(`/constructorDocuments`);
  };

  const onChangeRole = (e) => {
    const { name, checked } = e.target;

    const initialRoles = [...documentSection.roles];
    const newRoles = checked
      ? [...initialRoles, name]
      : initialRoles.filter((item) => item !== name);

    setDocumentSection({ ...documentSection, roles: newRoles });
  };

  const handleAction = async () => {
    if (documentSection.id) {
      if (!areArraysEqual(documentSection?.roles, data?.roles)) {
        for (let role of documentSection?.roles) {
          if (!data?.roles?.includes(role)) {
            await handleChangeRole(role, true);
          }
        }
        for (let role of data?.roles) {
          if (!documentSection?.roles?.includes(role)) {
            await handleChangeRole(role, false);
          }
        }
      }
      handleUpdate(documentSection);
    } else {
      handleCreate(documentSection).then((res) => {
        if (res?.data?.id) {
          navigate(`/constructorDocuments/${res.data.id}`);
        }
      });
    }
  };

  if (isError && !data) {
    return (
      <Container $paddingY={24}>
        <div>{t('Error')}</div>
      </Container>
    );
  }

  const isDisabled =
    !documentSection?.name ||
    (documentSection?.name === data?.name &&
      documentSection.name_RU === data?.name_RU &&
      documentSection.name_LV === data?.name_LV &&
      areArraysEqual(documentSection?.roles, data?.roles));

  if (isLoading) return <div>{t('Loading')}...</div>;

  return (
    <Container $paddingY={20}>
      <Box
        $isNew={true}
        styles={{
          body: {
            padding: '16px'
          }
        }}
      >
        <ConstructorForm>
          <Form.Item label={t('Name')}>
            <Input name='name' value={documentSection.name} onChange={onChange} />
          </Form.Item>
          {documentSection.id && (
            <>
              <Form.Item label={`${t('Name')} (RU)`}>
                <Input name='name_RU' value={documentSection.name_RU} onChange={onChange} />
              </Form.Item>
              <Form.Item label={`${t('Name')} (LV)`}>
                <Input name='name_LV' value={documentSection.name_LV} onChange={onChange} />
              </Form.Item>
            </>
          )}
          {documentSection.id && (
            <div style={{ display: 'inline-block' }}>
              <Form.Item label={t('Roles')}>
                <div style={{ display: 'inline-block' }}>
                  <Checkbox
                    name='PERSONAL'
                    checked={documentSection?.roles?.includes('PERSONAL')}
                    onChange={onChangeRole}
                    style={{ marginRight: '15px' }}
                  >
                    {t('PERSONAL')}
                  </Checkbox>
                  <Checkbox
                    name='COMPANY'
                    checked={documentSection?.roles?.includes('COMPANY')}
                    onChange={onChangeRole}
                    style={{ marginRight: '15px' }}
                  >
                    {t('COMPANY')}
                  </Checkbox>
                  <Checkbox
                    name='AGENT'
                    checked={documentSection?.roles?.includes('AGENT')}
                    onChange={onChangeRole}
                    style={{ marginRight: '15px' }}
                  >
                    {t('AGENT')}
                  </Checkbox>
                  <Checkbox
                    name='RECOMMENDER'
                    checked={documentSection?.roles?.includes('RECOMMENDER')}
                    onChange={onChangeRole}
                    style={{ marginRight: '15px' }}
                  >
                    {t('ASSISTANT')}
                  </Checkbox>
                </div>
              </Form.Item>
            </div>
          )}

          <ActionBox>
            <Button
              onClick={handleAction}
              disabled={isDisabled}
              type='primary'
              loading={isUpdating}
            >
              {documentSection.id ? t('Update Document Section') : t('Create Document Section')}
            </Button>
            {documentSection.id && (
              <Popconfirm
                placement='topRight'
                title={t('Are you sure to delete this document section?')}
                onConfirm={onDelete}
                okText={t('Yes')}
                cancelText={t('No')}
              >
                <Button danger loading={isDeleting}>
                  {t('Delete Document Section')}
                </Button>
              </Popconfirm>
            )}
          </ActionBox>
        </ConstructorForm>
      </Box>

      {documentSection.id && (
        <Container $paddingY={20}>
          <NestedBox>
            <DocumentTypeList documentSectionId={id} />

            {showCreateNew ? (
              <DocumentType onCreated={() => setShowCreateNew(false)} />
            ) : (
              <Button onClick={() => setShowCreateNew(true)}>{t('Add new Type')}</Button>
            )}
          </NestedBox>
        </Container>
      )}
    </Container>
  );
};
